"use client";

/**
 * Read + restore hooks for the entity audit log.
 *
 * Codes against `lib/audit/api-contract.ts` only — never against the route
 * implementation — so the UI and the routes can be built independently.
 *
 * Routes (per the plan's file table):
 *   GET  /api/audit/[entityType]/[entityId]?beforeSeq=<n>   paged, newest first
 *   POST /api/audit/restore                                 mode: plan | apply
 */

import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
} from "@tanstack/react-query";
import { useAuth } from "@west-tennessee-consulting/ui";

import {
  isRestoreApplied,
  parseAuditHistoryResponse,
  parseRestoreResponse,
} from "@/lib/audit/api-contract";
import type {
  AuditHistoryResponse,
  AuditableEntityType,
  RestoreRequest,
  RestoreResponse,
} from "@/lib/audit/api-contract";

/** Cursor into older history. `null` = the newest page. */
export type AuditHistoryCursor = number | null;

export const ENTITY_AUDIT_HISTORY_KEY = "entity-audit-history" as const;

export function entityAuditHistoryQueryKey(
  entityType: AuditableEntityType,
  entityId: string | null
): readonly [typeof ENTITY_AUDIT_HISTORY_KEY, AuditableEntityType, string | null] {
  return [ENTITY_AUDIT_HISTORY_KEY, entityType, entityId];
}

/**
 * The plan the user confirmed no longer matches the record — it moved while the
 * dialog was open. Never retried silently: the caller re-fetches the plan and
 * tells the user it changed.
 */
export class AuditPlanStaleError extends Error {
  readonly code = "plan-hash-mismatch";

  constructor(message: string) {
    super(message);
    this.name = "AuditPlanStaleError";
  }
}

/** Parse a response body that may not be JSON at all (proxy/HTML error pages). */
function parseJsonOrNull(raw: string): unknown {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    // Not JSON — there is nothing better to surface than the status code, so
    // fall back to it rather than showing the user raw markup.
    return null;
  }
}

/**
 * User-facing message for a failed request. Routes return
 * `{ error, code }` (USER_FACING_API_ERRORS), so prefer `error` and fall back
 * to the status code — never a raw Postgres message.
 */
async function readErrorMessage(response: Response): Promise<string> {
  const fallback = `Request failed (${response.status})`;
  const body = parseJsonOrNull(await response.text());
  if (body && typeof body === "object" && "error" in body) {
    const { error } = body as { error?: unknown };
    if (typeof error === "string" && error.trim().length > 0) return error;
  }
  return fallback;
}

function authHeaders(token: string | null): HeadersInit {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface UseEntityAuditHistoryOptions {
  entityType: AuditableEntityType;
  /** Null while the modal has no entity yet — the query stays disabled. */
  entityId: string | null;
  /** Do not fetch history for a closed modal. */
  enabled?: boolean;
}

export type EntityAuditHistoryQuery = UseInfiniteQueryResult<
  InfiniteData<AuditHistoryResponse, AuditHistoryCursor>,
  Error
>;

/**
 * Paged history, newest first. Page 1 is one S3 GET by design; `Load older
 * history` is what pays for the LIST + sealed months, so it is never
 * pre-fetched.
 */
export function useEntityAuditHistory({
  entityType,
  entityId,
  enabled = true,
}: UseEntityAuditHistoryOptions): EntityAuditHistoryQuery {
  const { getAuthToken } = useAuth();

  return useInfiniteQuery<
    AuditHistoryResponse,
    Error,
    InfiniteData<AuditHistoryResponse, AuditHistoryCursor>,
    readonly [typeof ENTITY_AUDIT_HISTORY_KEY, AuditableEntityType, string | null],
    AuditHistoryCursor
  >({
    queryKey: entityAuditHistoryQueryKey(entityType, entityId),
    enabled: enabled && Boolean(entityId),
    initialPageParam: null,
    queryFn: async ({ pageParam, signal }) => {
      if (!entityId) throw new Error("No entity selected");
      const token = await getAuthToken();
      const query = pageParam === null ? "" : `?beforeSeq=${encodeURIComponent(String(pageParam))}`;
      const response = await fetch(
        `/api/audit/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}${query}`,
        { headers: authHeaders(token), cache: "no-store", signal }
      );
      if (!response.ok) throw new Error(await readErrorMessage(response));
      return parseAuditHistoryResponse(await response.json());
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 30_000,
  });
}

export type AuditRestoreMutation = UseMutationResult<
  RestoreResponse,
  Error,
  RestoreRequest
>;

/**
 * Plan or apply a restore. `mode: "plan"` never writes; `mode: "apply"` does,
 * and must echo the `planHash` the user actually saw — a mismatch comes back
 * 409 and surfaces as {@link AuditPlanStaleError}.
 *
 * A successful apply invalidates the history query: the restore lands as a NEW
 * event (`source: "revert"`), because the log is append-only.
 */
export function useAuditRestore(
  entityType: AuditableEntityType,
  entityId: string | null
): AuditRestoreMutation {
  const { getAuthToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<RestoreResponse, Error, RestoreRequest>({
    mutationFn: async (request) => {
      const token = await getAuthToken();
      const response = await fetch("/api/audit/restore", {
        method: "POST",
        headers: { ...authHeaders(token), "Content-Type": "application/json" },
        body: JSON.stringify(request),
      });
      if (response.status === 409) {
        throw new AuditPlanStaleError(await readErrorMessage(response));
      }
      if (!response.ok) throw new Error(await readErrorMessage(response));
      return parseRestoreResponse(await response.json());
    },
    onSuccess: (response) => {
      if (isRestoreApplied(response)) {
        void queryClient.invalidateQueries({
          queryKey: entityAuditHistoryQueryKey(entityType, entityId),
        });
      }
    },
  });
}
